import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { AppState } from 'src/app/app.state';
import { UserService } from 'src/app/core/services/user.service';
import { GlobalSettingsValueRecognitionDialogComponent } from 'src/app/roles/da-engineer/global-settings/components/global-settings-value-recognition-dialog/global-settings-value-recognition-dialog.component';
import {
  clearReleaseEmailResult,
  loadHasUserGlobalAdminAccess,
  loadUserConfig,
  sendReleaseEmail,
  setUserConfigIsEmailDisabled,
  updateUserConfig,
} from '../../store/global-settings.actions';
import {
  selectHasUserGlobalAdminAccess,
  selectReleaseEmailResult,
  selectUserConfigForm,
} from '../../store/global-settings.selectors';
import {
  SEND_RELEASE_EMAIL_OPTIONS_ARRAY,
  DELIVERABLE_TEMPLATES_ROUTE,
  MANDATORY_CHECKS_TEMPLATES_ROUTE,
  PEER_POOL_DISCIPLINES_ROUTE,
  BUSINESS_UNITS_ROUTE,
  RISK_ASSESSMENT_ROUTE,
  ASSIGNMENTS_ROUTE,
} from '../../global-settings.constants';
import { GlobalAdminAccessDialogComponent } from 'src/app/roles/da-engineer/global-settings/components/global-admin-access-dialog/global-admin-access-dialog.component';
import { ExceptionAccessDialogComponent } from 'src/app/roles/da-engineer/global-settings/components/exception-access-dialog/exception-access-dialog.component';
import { SharePointUrls } from 'src/app/shared/models/autogenerated';
import { EntityCardOptionSelect } from 'src/app/shared/models/entity-card-option-select.model';
import { filter, take } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-global-settings',
  templateUrl: './global-settings.component.html',
  styleUrls: ['./global-settings.component.scss'],
})
export class GlobalSettingsComponent implements OnInit {
  readonly MANDATORY_CHECKS_TEMPLATES_ROUTE = MANDATORY_CHECKS_TEMPLATES_ROUTE;
  readonly PEER_POOL_DISCIPLINES_ROUTE = PEER_POOL_DISCIPLINES_ROUTE;
  readonly DELIVERABLE_TEMPLATES_ROUTE = DELIVERABLE_TEMPLATES_ROUTE;
  readonly BUSINESS_UNITS_ROUTE = BUSINESS_UNITS_ROUTE;
  readonly RISK_ASSESSMENT_ROUTE = RISK_ASSESSMENT_ROUTE;
  readonly ASSIGNMENTS_ROUTE = ASSIGNMENTS_ROUTE;
  isUserDAEngineer: boolean = false;
  isSendEmailNotificationsEnabled = false;
  userConfigForm$ = this.store.select(selectUserConfigForm);
  hasUserGlobalAdminAccess$ = this.store.select(selectHasUserGlobalAdminAccess);
  showUserManagement: boolean = true;
  showEmailSettings: boolean = true;
  showDefaultTemplates: boolean = true;
  sendReleaseEmailOptions: EntityCardOptionSelect[] =
    SEND_RELEASE_EMAIL_OPTIONS_ARRAY;

  constructor(
    private userService: UserService,
    private store: Store<AppState>,
    private dialog: MatDialog
  ) {
    this.store.dispatch(loadUserConfig());
    this.store.dispatch(loadHasUserGlobalAdminAccess());
  }

  ngOnInit(): void {
    this.isUserDAEngineer = this.userService.isDAEngineer();
  }

  openValueRecognition() {
    this.dialog.open(GlobalSettingsValueRecognitionDialogComponent, {
      panelClass: 'global-settings-value-recognition-dialog',
      maxWidth: 'unset',
      data: {},
      disableClose: true,
    });
  }

  editGlobalAdminAccess() {
    this.dialog.open(GlobalAdminAccessDialogComponent, {
      panelClass: 'global-admin-access-dialog',
      maxWidth: 'unset',
      disableClose: true,
    });
  }

  editExceptionAccess() {
    this.dialog.open(ExceptionAccessDialogComponent, {
      panelClass: 'exception-access-dialog',
      maxWidth: 'unset',
      disableClose: true,
    });
  }

  onSendEmailNotificationChange(sendEmails: boolean) {
    const isEmailDisabled = !sendEmails;
    this.store.dispatch(setUserConfigIsEmailDisabled({ isEmailDisabled }));
    this.store.dispatch(updateUserConfig());
  }

  openPeerPoolDashboard() {
    const peerPoolDashboardURL = `${SharePointUrls.Chevron}sites/DTSEU/SitePages/Design-Engineering-Peer-Pool-Dashboard.aspx`;
    window.open(peerPoolDashboardURL, '_blank');
  }

  changeExpansion(section: string) {
    switch (section) {
      case 'userManagement':
        this.showUserManagement = !this.showUserManagement;
        break;

      case 'emailSettings':
        this.showEmailSettings = !this.showEmailSettings;
        break;

      case 'defaultTemplates':
        this.showDefaultTemplates = !this.showDefaultTemplates;
        break;

      default:
        break;
    }
  }

  onSendReleaseEmailOptionSelect(option: EntityCardOptionSelect): void {
    this.store.dispatch(sendReleaseEmail({ role: option.value! }));

    this.store
      .pipe(
        select(selectReleaseEmailResult),
        filter((response) => !!response),
        take(1)
      )
      .subscribe((response) => {
        const to = response?.to.join('; ') ?? '';
        const subject = encodeURIComponent(response?.subject ?? '');
        const body = encodeURIComponent(response?.body ?? '');
        const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
        navigator.clipboard.writeText(to);
        window.location.href = mailtoLink;
        this.store.dispatch(clearReleaseEmailResult());
      });
  }
}
